import * as React from 'react';
import './create-staff.scss';
import {
  Button,
  Drawer,
  FieldGroup,
  FieldLabel,
  FieldMessage,
  Input,
  Icon as OldIcon,
} from '@teamsnap/teamsnap-ui';
import { useMutation } from '@apollo/client';
import { MUTATION as UpdateStaff } from '../../core/gql/mutations/update-staff';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import { MUTATION as INVITE_STAFF } from '../../core/gql/mutations/invite-staff';
import { QUERY_WITH_PROGRAM as SeasonsQuery } from '../../core/gql/queries/seasons';
import { QUERY as ProgramsQuery } from '../../core/gql/queries/programs';
import { useQuery } from '@apollo/client';
import { useLazyQuery } from '@apollo/client';
import { QUERY_WITH_DIVISIONS as DivisionsQuery } from '../../core/gql/queries/divisions-and-teams';
import RolesAndPermissions, {
  RefProps,
  RolesAndPermissionsProps,
  defaultRole,
  Role,
} from 'frontend-toolkit/src/components/RolesAndPermissions/RolesPermissions';
import useChurnZero from 'components/common/useChurnZero';
import { sortRoles } from 'auth';

type FormData = {
  firstName: string;
  firstNameError: boolean;
  lastName: string;
  lastNameError: boolean;
  emailAddress: string;
  emailAddressError: boolean;
  roles: RolesAndPermissionsProps[];
};

type Props = {
  orgId: number;
  open: boolean;
  close: (reload: boolean) => any;
  editStaff?: {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    staffRoles: {
      type: Role;
      programs: { id; name }[];
      season: { id };
      divisions: { id; name }[];
    }[];
  };
};

const showMessage = (message, isError) =>
  reactiveFeedbackMessage({
    message,
    isError,
    autoclose: true,
  });

const initialValues = {
  firstName: '',
  firstNameError: false,
  lastName: '',
  lastNameError: false,
  emailAddress: '',
  emailAddressError: false,
  roles: [defaultRole],
};

const CreateStaffSidebar = ({ orgId, editStaff, open, close }: Props) => {
  const { setChurnZeroAttribute } = useChurnZero({ organizationId: orgId });

  const [formData, setFormData] = React.useState<FormData>(initialValues);
  const RolesAndPermissionsRef = React.useRef<RefProps>(null);

  const [inviteStaff] = useMutation(INVITE_STAFF);
  const [updateStaff] = useMutation(UpdateStaff);

  React.useEffect(() => {
    const work = async () => {
      if (editStaff) {
        const { staffRoles, ...staff } = editStaff;

        await RolesAndPermissionsRef?.current.loadDivisions(
          ...editStaff.staffRoles.map((role) => role.season?.id)
        );

        setFormData({
          ...initialValues,
          ...staff,
          roles: editStaff.staffRoles.map((x) => ({
            role: x.type,
            roleError: false,
            season: `${x.season?.id || ''}`,
            seasonError: false,
            programs: (x.programs || []).map((y) => `${y.id}`),
            programsError: false,
            divisions: (x.divisions || []).map((y) => `${y.id}`),
            divisionsError: false,
          })),
        });
      }
    };
    work();
  }, []);

  const { data: programsData } = useQuery(ProgramsQuery, {
    variables: { organizationId: +orgId },
  });
  const { data: seasonsData } = useQuery(SeasonsQuery, {
    variables: { organizationId: +orgId },
  });
  const [queryDivisions, { loading: isLoadingDivisions }] = useLazyQuery(
    DivisionsQuery,
    {
      fetchPolicy: 'cache-first',
    }
  );

  const programs = (programsData?.programs || []).map((program) => ({
    value: `${program.id}`,
    label: program.name,
  }));

  const seasons = (seasonsData?.seasons || []).map((x) => ({
    value: x.id,
    label: `${x.name}${x.program?.name ? ` (${x.program?.name})` : ''}`,
  }));

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = {
      ...formData,
      firstNameError: !formData.firstName,
      lastNameError: !formData.lastName,
      emailAddressError: !formData.emailAddress,
      roles: formData.roles.map((x) => ({
        ...x,
        roleError: !x.role,
        programsError:
          [Role.ProgramAdmin, Role.ProgramCoordinator].includes(
            x.role as Role
          ) && !x.programs.length,
        seasonError: x.role === Role.DivisionCoordinator && !x.season,
        divisionsError:
          x.role === Role.DivisionCoordinator && !x.divisions.length,
      })),
    };

    setFormData(form);
    if (
      form.firstNameError ||
      form.lastNameError ||
      form.emailAddressError ||
      form.roles.some(
        (x) =>
          x.roleError || x.programsError || x.seasonError || x.divisionsError
      )
    ) {
      return;
    }

    const staffRoles = formData.roles.map((x) => ({
      type: x.role,
      programIds: x.programs,
      divisionIds: x.divisions,
    }));

    const highestRole =  sortRoles(staffRoles.map((x) => x.type as Role))[0]

    if (editStaff) {
      try {
        await updateStaff({
          variables: {
            staffId: editStaff.id,
            organizationId: orgId,
            staffRoles,
          },
        });
        showMessage('Staff member successfully updated', false);
        close(true);
        setChurnZeroAttribute(
          formData.firstName,
          formData.lastName,
          formData.emailAddress,
          highestRole
        );
      } catch (e) {
        showMessage(
          'Oops! We dropped the ball. Please try to save the staff member again..',
          true
        );
      }
    } else {
      const resp = await inviteStaff({
        variables: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.emailAddress,
          orgId: orgId,
          staffRoles,
        },
      });
      if (resp.data?.inviteUser?.id) {
        setFormData(initialValues);
        showMessage('Your invitation is on it\'s way!', false);
        close(true);
        setChurnZeroAttribute(
          formData.firstName,
          formData.lastName,
          formData.emailAddress,
          highestRole
        );
      } else {
        showMessage(
          'Oops! We dropped the ball. Please send your invitation again.',
          true
        );
        console.error('Unable to invite new user to organization.');
      }
    }
  };

  return (
    <Drawer
      testId={`${editStaff ? 'edit' : 'create'}-staff-sidebar`}
      showOverlay
      open={open}
      closeFn={close}
      style={{
        maxWidth: '375px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1050,
      }}
      mods="u-size12of24"
    >
      <div className="u-flex u-flexJustifyBetween">
        <p
          className="u-fontSizeLg u-textBold"
          data-testid="staff-slideout-header"
        >
          {editStaff ? 'Edit' : 'Invite'} staff member
        </p>
        <OldIcon
          mods="pointer"
          name="dismiss"
          otherProps={{ onClick: () => close(false) }}
        />
      </div>

      <p className="u-spaceEndsMd">
        Each staff member controls their own contact information through their{' '}
        <a href="https://accounts.teamsnap.com/">account settings.</a>
      </p>

      <form id="create-staff-form" onSubmit={onSubmit}>
        <div>
          <FieldGroup status={formData.firstNameError ? 'error' : undefined}>
            <FieldLabel name="firstName">
              First Name<span className="sui-text-red-40">*</span>
            </FieldLabel>
            <Input
              name="firstName"
              size="large"
              inputProps={{
                disabled: !!editStaff,
                value: formData.firstName,
                onChange: (e) =>
                  setFormData({ ...formData, firstName: e.target.value }),
              }}
            />
            <FieldMessage
              status={formData.firstNameError ? 'error' : undefined}
            >
              {formData.firstNameError ? 'First name is required' : undefined}
            </FieldMessage>
          </FieldGroup>

          <FieldGroup
            status={formData.lastNameError ? 'error' : undefined}
            mods="u-spaceTopMd"
          >
            <FieldLabel name="lastName">
              Last Name<span className="sui-text-red-40">*</span>
            </FieldLabel>
            <Input
              name="lastName"
              size="large"
              inputProps={{
                disabled: !!editStaff,
                value: formData.lastName,
                onChange: (e) =>
                  setFormData({ ...formData, lastName: e.target.value }),
              }}
            />
            <FieldMessage status={formData.lastNameError ? 'error' : undefined}>
              {formData.lastNameError ? 'Last name is required' : undefined}
            </FieldMessage>
          </FieldGroup>

          <FieldGroup
            status={formData.emailAddressError ? 'error' : undefined}
            mods="u-spaceTopMd"
          >
            <FieldLabel name="emailAddress">
              Email address<span className="sui-text-red-40">*</span>
            </FieldLabel>
            <Input
              name="emailAddress"
              type="email"
              size="large"
              inputProps={{
                disabled: !!editStaff,
                value: formData.emailAddress,
                onChange: (e) =>
                  setFormData({ ...formData, emailAddress: e.target.value }),
              }}
            />
            <FieldMessage
              status={formData.emailAddressError ? 'error' : undefined}
            >
              {formData.emailAddressError
                ? 'Email address is required'
                : undefined}
            </FieldMessage>
          </FieldGroup>

          <RolesAndPermissions
            ref={RolesAndPermissionsRef}
            programs={programs}
            seasons={seasons}
            roles={formData.roles}
            setRoles={(roles) => setFormData({ ...formData, roles })}
            queryDivisions={queryDivisions}
            isLoadingDivisions={isLoadingDivisions}
          />
        </div>

        <div className="create-staff-buttons">
          <Button
            testId="cancel-button"
            mods="u-spaceRightSm"
            onClick={() => close(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            testId="invite-button"
            mods="Button Button--primary"
          >
            {editStaff ? 'Save' : 'Invite'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateStaffSidebar;
